/**
 * React Query Client Configuration
 * Centralized query client with optimized settings
 * 
 * @task FND-FE-002: State Management Foundation
 */

import { QueryClient } from '@tanstack/react-query';
import { defaultQueryOptions } from './defaultOptions';

/**
 * Create a new QueryClient instance with optimized defaults
 * 
 * @param options - Optional overrides for query client config
 */
export function createQueryClient(options?: { enableDevTools?: boolean }): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        ...defaultQueryOptions.queries,
      },
      mutations: {
        ...defaultQueryOptions.mutations,
      },
    },
  });
}

/**
 * Global QueryClient instance
 * Use this for the QueryClientProvider
 */
export const queryClient = createQueryClient();

/**
 * Utility to invalidate multiple queries at once
 */
export function invalidateQueries(queryKeys: string[][]): Promise<void> {
  return Promise.all(
    queryKeys.map(key => queryClient.invalidateQueries({ queryKey: key }))
  ).then(() => undefined);
}

/**
 * Prefetch data for a query
 */
export function prefetchQuery<T>(
  queryKey: string[],
  fetcher: () => Promise<T>,
  options?: { staleTime?: number }
): Promise<void> {
  return queryClient.prefetchQuery({
    queryKey,
    queryFn: fetcher,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Cancel all active queries
 */
export function cancelQueries(): Promise<void> {
  return queryClient.cancelQueries();
}

/**
 * Clear all cached data
 */
export function clearCache(): void {
  queryClient.clear();
}

export default queryClient;
